/**
 * Admin Portal → Users: one user's paid-access controls — a tier override (Essentials / Chat+ /
 * Pro / Unlimited tester), a Full & Free Evaluation, and extending or revoking one.
 *
 * Codes PR-1b (2026-09-29, review N-e): the server answers 403 to a support or viewer session on
 * every one of these writes (routers/beta_user_admin.py, ENTITLEMENT_GRANT_ROLES). So, the way
 * the Codes and partner pages hide their write tools, a session that cannot write sees the
 * user's current access as plain text — no select to pick from. `writable` comes from the
 * server's own answer (useAdminRole: fail closed while it is unknown or failed).
 *
 * Web-only (the admin SPA).
 */

import React from 'react';

export type PaidAccessUser = {
  id: string;
  subscription?: { evaluationType?: string | null; adminTierOverride?: string | null } | null;
};

export const TIER_OVERRIDE_LABELS: Record<string, string> = {
  '': 'Standard',
  basic: 'Essentials Tester',
  full: 'Chat+ Tester',
  extended: 'Pro Tester',
  unlimited: 'Unlimited Tester',
};

/** The user's current paid access, in the words the select shows. */
export function currentAccessLabel(user: PaidAccessUser): string {
  if (user.subscription?.evaluationType === 'full_free_pro') return 'Pro Evaluation';
  return TIER_OVERRIDE_LABELS[user.subscription?.adminTierOverride || ''] || 'Standard';
}

export const UserPaidAccessControls: React.FC<{
  user: PaidAccessUser;
  userName: string;
  writable: boolean;
  onGrantEvaluation: (userId: string, userName: string) => void;
  onExtendEvaluation: (userId: string, userName: string) => void;
  onRevokeEvaluation: (userId: string, userName: string) => void;
  onTierOverride: (userId: string, userName: string, overrideTier: string | null) => void;
}> = ({ user, userName, writable, onGrantEvaluation, onExtendEvaluation, onRevokeEvaluation, onTierOverride }) => {
  if (!writable) {
    return (
      <span
        className="text-xs px-1.5 py-1 rounded border"
        style={{ color: 'var(--cedar-dark)', borderColor: 'var(--border)' }}
        title="Changing paid access needs an admin or super admin"
        data-testid="paid-access-readonly"
      >
        {currentAccessLabel(user)}
      </span>
    );
  }
  const isOnEvaluation = user.subscription?.evaluationType === 'full_free_pro';
  if (isOnEvaluation) {
    return (
      <select
        defaultValue=""
        onChange={(e) => {
          const val = e.target.value;
          e.target.value = '';
          if (val === 'extend') onExtendEvaluation(user.id, userName);
          else if (val === 'revoke') onRevokeEvaluation(user.id, userName);
        }}
        className="text-xs px-1.5 py-1 rounded border cursor-pointer"
        style={{ backgroundColor: 'var(--cedar-subtle)', color: 'var(--cedar-dark)', borderColor: 'var(--cedar-primary)' }}
        title="Evaluation active"
      >
        <option value="" disabled>Pro Evaluation</option>
        <option value="extend">Extend Evaluation</option>
        <option value="revoke">Revoke Evaluation</option>
      </select>
    );
  }
  return (
    <select
      value={user.subscription?.adminTierOverride || ''}
      onChange={(e) => {
        const val = e.target.value;
        if (val === 'evaluation_grant') {
          e.target.value = user.subscription?.adminTierOverride || '';
          onGrantEvaluation(user.id, userName);
        } else {
          onTierOverride(user.id, userName, val === '' ? null : val);
        }
      }}
      className="text-xs px-1.5 py-1 rounded border cursor-pointer"
      style={{ backgroundColor: 'var(--cedar-limestone)', color: 'var(--cedar-dark)', borderColor: 'var(--border)' }}
      title="Set tier override"
    >
      <option value="">Standard</option>
      <option value="evaluation_grant">Full &amp; Free Evaluation (Pro)</option>
      <option disabled>──────────</option>
      <option value="basic">Essentials Tester</option>
      <option value="full">Chat+ Tester</option>
      <option value="extended">Pro Tester</option>
      <option value="unlimited">Unlimited Tester</option>
    </select>
  );
};
